import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2,
  Facebook, Linkedin, Twitter, Users, HardHat, Building2,
  FileCheck, Headphones, Shield, Menu, X, Search
} from 'lucide-react';
import industriesData from '../data/industriesData.json';
import HeroSwitch from './HeroSwitch';
import PainPointBand from './PainPointBand';
import WhyAdel from './WhyAdel';
import IndustriesBento from './IndustriesBento';
import ProcessJourney from './ProcessJourney';
import ByTheNumbers from './ByTheNumbers';
import Testimonials from './Testimonials';
import PartnerMarquee from './PartnerMarquee';
import DualCtaStrip from './DualCtaStrip';
import SiteFooter from './SiteFooter';
import CVSubmissionForm from './CVSubmissionForm';
import JobsList from './JobsList';
import './LandingPage.css';

const VIEW_PARK_TOWERS_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=View+Park+Towers,+Utaali+Lane,+Nairobi';

const INDUSTRIES = industriesData;

const MOBILE_BREAKPOINT = 768;

const LandingPage = ({ page = 'home' }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerShrunk, setHeaderShrunk] = useState(false);
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);
  const [footerLogoLoaded, setFooterLogoLoaded] = useState(false);
  const { slug: industrySlug } = useParams();

  const activeIndustry =
    page === 'industry-detail'
      ? INDUSTRIES.find((ind) => ind.slug === industrySlug)
      : null;

  const isActivePath = (path) => location.pathname === path;

  // Sync isMobile with viewport (mobile = single scrollable page + hash nav)
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // On home, scroll to section when hash is present (mobile anchor nav)
  useEffect(() => {
    if (page !== 'home' || !location.hash) return;
    const id = location.hash.slice(1);
    if (!id) return;
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [page, location.pathname, location.hash]);

  // Shrink header on scroll: hysteresis to prevent wiggling at threshold
  useEffect(() => {
    let ticking = false;
    const SHRINK_AT = 120;
    const EXPAND_AT = 50;

    const updateShrink = () => {
      const y = window.scrollY;
      setHeaderShrunk((prev) => {
        if (prev) return y > EXPAND_AT;
        return y > SHRINK_AT;
      });
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateShrink);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply animate-in to all sections on load so the whole page loads at once (no scroll-triggered reveal)
  useEffect(() => {
    const selector = '.section-header, .about-content, .about-text, .about-images, .service-card, .stat-card, .industry-tile, .info-card-compact, .step-item, .industry-detail-card, .industry-detail-cta-block, .industry-image-card, .industry-stats-card, .contact-email-cta, .cv-email-cta';
    const showAll = () => {
      document.querySelectorAll(selector).forEach((el) => el.classList.add('animate-in'));
    };
    const rafId = requestAnimationFrame(showAll);
    return () => cancelAnimationFrame(rafId);
  }, [page]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  const serviceList = [
    {icon: Users, title: "Skilled & Semi-Skilled Recruitment", desc: "Access to quality professionals across industries."},
    {icon: HardHat, title: "Trade-Based Recruitment", desc: "Specialized recruitment for construction and technical roles."},
    {icon: Building2, title: "Project & Bulk Hiring", desc: "Large-scale recruitment for major projects."},
    {icon: FileCheck, title: "Documentation & Mobilization", desc: "Complete coordination of visas and travel."},
    {icon: Headphones, title: "Post-Deployment Support", desc: "Ongoing support after placement."},
    {icon: Shield, title: "Compliance Management", desc: "Full regulatory compliance."}
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className={`header ${headerShrunk ? 'header--shrunk' : ''}`}>
        <div className="top-bar">
          <div className="container">
            <div className="top-bar-content">
              <div className="contact-info">
                <a href="tel:+254117222777" className="contact-item">
                  <Phone size={14} />
                  <span>+254 117 222 777</span>
                </a>
                <a href="mailto:info@adelgroups.com" className="contact-item">
                  <Mail size={14} />
                  <span>info@adelgroups.com</span>
                </a>
                <a
                  href={VIEW_PARK_TOWERS_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-item location-item"
                  aria-label="Open View Park Towers, Utaali Lane, Nairobi in Google Maps"
                >
                  <MapPin size={14} />
                  <span>Nairobi, Kenya</span>
                </a>
              </div>
              <div className="hours">
                <Clock size={14} />
                <span>Mon &ndash; Sat &middot; 8am &ndash; 5pm EAT</span>
              </div>
            </div>
          </div>
        </div>

        <nav className="main-nav">
          <div className="container">
            <div className="nav-content">
              <Link to="/" className="logo" aria-label="Adel Group home">
                <img
                  src="/adel-logo-light.png"
                  alt="Adel Group – Recruitment & Manpower Solutions"
                  className="logo-image"
                  onLoad={() => setLogoImageLoaded(true)}
                />
                <span className="logo-fallback" style={{ display: logoImageLoaded ? 'none' : 'inline-block' }}>
                  <span className="logo-text">ADEL</span>
                  <span className="logo-text-accent">GROUP</span>
                </span>
              </Link>
              
              {/* Desktop Navigation */}
              <ul className="nav-links desktop-nav">
                <li>
                  <Link to="/" className={`nav-link ${isActivePath('/') ? 'active' : ''}`}>
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className={`nav-link ${isActivePath('/about') ? 'active' : ''}`}>
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/industries" className={`nav-link ${isActivePath('/industries') ? 'active' : ''}`}>
                    Industries
                  </Link>
                </li>
                <li>
                  <Link to="/services" className={`nav-link ${isActivePath('/services') ? 'active' : ''}`}>
                    Services
                  </Link>
                </li>
                <li>
                  <Link to="/jobs" className={`nav-link ${isActivePath('/jobs') ? 'active' : ''}`}>
                    Open Roles
                  </Link>
                </li>
                <li>
                  <Link to="/job-seekers" className={`nav-link ${isActivePath('/job-seekers') ? 'active' : ''}`}>
                    Submit CV
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className={`nav-link ${isActivePath('/contact') ? 'active' : ''}`}>
                    Contact
                  </Link>
                </li>
              </ul>

              <div className="nav-cta desktop-nav">
                <Link to="/contact" className="btn-primary">
                  Request Talent
                </Link>
              </div>

              {/* Contact icons in main bar (mobile: only icons; desktop: optional quick access) */}
              <div className="header-contact-icons">
                <a href="tel:+254117222777" className="header-contact-icon" aria-label="Call us">
                  <Phone size={20} />
                </a>
                <a href={VIEW_PARK_TOWERS_MAPS_URL} target="_blank" rel="noopener noreferrer" className="header-contact-icon" aria-label="View location on map">
                  <MapPin size={20} />
                </a>
                <a href="mailto:info@adelgroups.com" className="header-contact-icon" aria-label="Email us">
                  <Mail size={20} />
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button className="mobile-menu-button" onClick={toggleMobileMenu} aria-label="Toggle menu">
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
              <div className="mobile-menu-overlay" onClick={closeMobileMenu}>
                <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
                  <ul className="mobile-nav-links">
                    <li>
                      <Link to={isMobile ? '/#home' : '/'} className="mobile-nav-link" onClick={closeMobileMenu}>
                        Home
                      </Link>
                    </li>
                    <li>
                      <Link to={isMobile ? '/#about' : '/about'} className="mobile-nav-link" onClick={closeMobileMenu}>
                        About
                      </Link>
                    </li>
                    <li>
                      <Link to={isMobile ? '/#industries' : '/industries'} className="mobile-nav-link" onClick={closeMobileMenu}>
                        Industries
                      </Link>
                    </li>
                    <li>
                      <Link to={isMobile ? '/#services' : '/services'} className="mobile-nav-link" onClick={closeMobileMenu}>
                        Services
                      </Link>
                    </li>
                    <li>
                      <Link to="/jobs" className="mobile-nav-link" onClick={closeMobileMenu}>
                        Open Roles
                      </Link>
                    </li>
                    <li>
                      <Link to={isMobile ? '/#job-seekers' : '/job-seekers'} className="mobile-nav-link" onClick={closeMobileMenu}>
                        Submit CV
                      </Link>
                    </li>
                    <li>
                      <Link to={isMobile ? '/#contact' : '/contact'} className="mobile-nav-link" onClick={closeMobileMenu}>
                        Contact
                      </Link>
                    </li>
                  </ul>
                  <div className="mobile-menu-contact">
                    <a href={VIEW_PARK_TOWERS_MAPS_URL} target="_blank" rel="noopener noreferrer" className="mobile-menu-contact-item" onClick={closeMobileMenu} aria-label="View on map">
                      <MapPin size={18} />
                      <span>Nairobi, Kenya</span>
                    </a>
                    <a href="tel:+254117222777" className="mobile-menu-contact-item" onClick={closeMobileMenu} aria-label="Call us">
                      <Phone size={18} />
                      <span>+254 117 222 777</span>
                    </a>
                    <a href="mailto:info@adelgroups.com" className="mobile-menu-contact-item" onClick={closeMobileMenu} aria-label="Email us">
                      <Mail size={18} />
                      <span>info@adelgroups.com</span>
                    </a>
                  </div>
                  <div className="mobile-cta-buttons">
                    <Link to={isMobile ? '/#contact' : '/contact'} className="btn-primary btn-mobile" onClick={closeMobileMenu}>
                      Request Talent
                    </Link>
                    <Link to={isMobile ? '/#job-seekers' : '/job-seekers'} className="btn-secondary btn-mobile" onClick={closeMobileMenu}>
                      Find a Job
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* 3D Globe hero: only on Home (auto-falls back to static poster on mobile/reduced-motion/save-data) */}
      {page === 'home' && <HeroSwitch />}

      {/* Pain-point band immediately after hero — frames the cross-border hiring problem */}
      {page === 'home' && <PainPointBand />}

      {/* Why Adel — 3-card strip (Connecting / Placing / Supporting) */}
      {page === 'home' && <WhyAdel />}

      {/* Industry Detail Page */}
      {page === 'industry-detail' && activeIndustry && (
      <section className="industry-detail-section">
        <div className="container">
          <div className="section-header">
            <h1 className="section-title">{activeIndustry.name}</h1>
            <p className="section-description">
              {activeIndustry.intro}
            </p>
          </div>

          <div className="industry-detail-layout">
            <div className="industry-detail-main">
              <div className="industry-detail-card">
                <h2 className="industry-detail-heading">Why Kenyan talent fits {activeIndustry.name}</h2>
                <ul className="industry-detail-strengths">
                  {activeIndustry.strengths.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>

              <div className="industry-detail-card">
                <h2 className="industry-detail-heading">How Adel Group supports {activeIndustry.name}</h2>
                <p className="industry-detail-text">
                  {activeIndustry.adelSupport}
                </p>
              </div>

              <div className="industry-detail-card">
                <h2 className="industry-detail-heading">Roles we commonly recruit</h2>
                <p className="industry-detail-text">
                  We support employers with both volume hiring and targeted placements in this industry. Here are some of the job titles we regularly recruit for:
                </p>
                <div className="industry-jobs-grid">
                  {activeIndustry.jobs.map((job) => (
                    <div key={job} className="industry-job-pill">
                      {job}
                    </div>
                  ))}
                </div>
              </div>

              <div className="industry-detail-cta">
                <div className="industry-detail-cta-block">
                  <h3>Hire Kenyan talent for {activeIndustry.name}</h3>
                  <p>
                    Share your manpower plan and we&apos;ll match you with quality candidates ready for deployment to the Middle East.
                  </p>
                  <div className="industry-detail-cta-actions">
                    <Link to="/contact" className="btn-hero-primary">
                      Discuss your requirements
                      <ArrowRight size={18} />
                    </Link>
                    <Link to="/job-seekers" className="btn-hero-secondary">
                      View candidate journey
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <aside className="industry-detail-sidebar">
              <div className="industry-image-card">
                <div className="industry-image-wrapper">
                  <img
                    src={activeIndustry.image}
                    alt={activeIndustry.name}
                    className="industry-detail-image"
                  />
                  <div className="industry-image-overlay"></div>
                </div>
                <p className="industry-image-caption">
                  Kenyan professionals supporting {activeIndustry.name.toLowerCase()} projects across the Middle East.
                </p>
              </div>

              <div className="industry-stats-card">
                <h3 className="industry-detail-heading">What employers value</h3>
                <ul className="industry-detail-strengths">
                  <li>Reliable attendance and strong work ethic.</li>
                  <li>Ability to follow site rules, safety standards, and procedures.</li>
                  <li>Openness to training, upskilling, and long-term contracts.</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
      )}

      {/* About Section — kept on /about only; Home now uses WhyAdel + DualCtaStrip */}
      {page === 'about' && (
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header">
            {page === 'about' ? <h1 className="section-title">About Us</h1> : <h2 className="section-title">About Us</h2>}
          </div>

          <div className="about-content">
            <div className="about-text">
              {page === 'home' ? (
                <p className="about-description">
                  ADEL Group is a Kenya-based recruitment agency connecting skilled professionals with reputable local and international employers. We specialize in sourcing and mobilizing qualified candidates through a transparent and compliant process. Our commitment is to deliver reliable manpower solutions while creating meaningful opportunities for growth.
                </p>
              ) : (
              <>
              <p className="about-description">
                Based in Nairobi, Kenya, Adel Group helps employers in the Middle East find the right talents from Kenya.
              </p>
              <p className="about-description">
                We manage the entire recruitment process—from sourcing candidates to documentation and deployment. This ensures candidates are properly prepared and ready for opportunities abroad.
              </p>
              <p className="about-description">
                We take time to understand workforce requirements and carefully select candidates who are verified, compliant, and job-ready. Our work is guided by clear processes, honest communication, and long-term relationships.
              </p>
              <p className="about-description">
                At Adel, we keep recruitment simple, transparent, and people-focused—connecting the right talent to the right opportunity.
              </p>
              </>
              )}
            </div>

            <div className="about-images">
              <img src="/people/about-team.jpg" alt="Professional Kenyan team" className="about-img about-img-1" loading="lazy" decoding="async" />
              <img src="/people/about-mena.jpg" alt="Kenyan professionals in the Middle East" className="about-img about-img-2" loading="lazy" decoding="async" />
            </div>
          </div>

          {/* How We Work Section - only on About page */}
          {page === 'about' && (
            <div className="how-we-work">
              <h3 className="how-we-work-title">How We Work</h3>
              <div className="steps-grid">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Understand Requirements</h4>
                    <p>We start by understanding job roles, workforce needs, and expectations to ensure the right fit from the beginning.</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Source & Match Candidates</h4>
                    <p>Candidates are sourced, interviewed, and matched to your requirements before selection.</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Documentation & Visa Stamping</h4>
                    <p>We manage documentation, medicals, visa submission, and deployment coordination to ensure a smooth process.</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Deployment & Support</h4>
                    <p>Candidates are deployed as planned, with ongoing coordination and post-deployment support when required.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Services Section — only on dedicated /services page */}
      {page === 'services' && (
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h1 className="section-title">What We Do</h1>
            <p className="section-description">
              At Adel Group, we manage recruitment from start to finish. Our focus is to make hiring and job placement clear, structured, and well-coordinated—across all types of roles and skill levels.
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon"><Users size={32} /></div>
              <h3 className="service-title">Manpower Recruitment</h3>
              <p className="service-description">
                We recruit unskilled, semi-skilled, and skilled manpower from Kenya based on actual workforce requirements. We take time to understand the role, working conditions, and expectations before selecting candidates who are suitable and ready to work.
              </p>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><Building2 size={32} /></div>
              <h3 className="service-title">Temporary & Project Staffing</h3>
              <p className="service-description">
                For short-term, project-based, or time-bound needs, we support employers with manpower aligned to project timelines. This includes sourcing the required number of workers and ensuring availability when projects start or scale.
              </p>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><HardHat size={32} /></div>
              <h3 className="service-title">Role-Based Recruitment</h3>
              <p className="service-description">
                Rather than limiting recruitment to specific job titles, we focus on role-based hiring. This allows us to recruit across different industries and job functions, depending on demand, while matching the right skills and experience to each role.
              </p>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><Shield size={32} /></div>
              <h3 className="service-title">Sourcing & Selection</h3>
              <p className="service-description">
                We match every candidate to your requirements before selection. Our goal is to put forward people who are verified, compliant, and job-ready.
              </p>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><FileCheck size={32} /></div>
              <h3 className="service-title">Documentation & Mobilization</h3>
              <p className="service-description">
                We manage documentation, medical processing, and coordination required for deployment. By handling these steps carefully, we help ensure smoother mobilization and clearer timelines.
              </p>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><Headphones size={32} /></div>
              <h3 className="service-title">Post-Deployment Support</h3>
              <p className="service-description">
                Our work continues even after deployment. We provide coordination support and assist with replacements when required, helping employers maintain workforce continuity.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Industries Bento — featured on Home for both desktop and mobile */}
      {page === 'home' && <IndustriesBento industries={INDUSTRIES} />}

      {/* Process Journey — featured on Home */}
      {page === 'home' && <ProcessJourney />}

      {/* By the Numbers — animated counters band */}
      {page === 'home' && <ByTheNumbers />}

      {/* Testimonials (placeholder — gated for Google Places API integration) */}
      {page === 'home' && <Testimonials />}

      {/* Partner logos marquee (placeholder slots until real logos arrive) */}
      {page === 'home' && <PartnerMarquee />}

      {/* Dual CTA strip — Employer + Candidate side-by-side */}
      {page === 'home' && <DualCtaStrip />}

      {/* Full Industries Grid — only on the dedicated Industries page */}
      {page === 'industries' && (
      <section id="industries" className="industries-section-new">
        <div className="container">
          <div className="section-header">
            <h1 className="section-title">Industries We Serve</h1>
            <p className="section-description">
              We support recruitment across a wide range of industries. Our role-based approach allows us to adapt to different workforce needs depending on the project, sector, and location.
            </p>
          </div>

          <div className="industries-tiles-grid">
            {INDUSTRIES.map((industry) => (
              <Link
                key={industry.slug}
                to={`/industries/${industry.slug}`}
                className="industry-tile"
              >
                <img src={industry.image} alt={industry.name} className="tile-image" />
                <div className="tile-overlay"></div>
                <h3 className="tile-title">{industry.name}</h3>
              </Link>
            ))}
          </div>

          <p className="industries-footer-note">
            We also support recruitment for other roles based on specific requirements.
          </p>
        </div>
      </section>
      )}

      {/* Public Jobs listings — only on dedicated /jobs page */}
      {page === 'jobs' && <JobsList />}

      {/* Job Seekers Section — only on dedicated /job-seekers page; home uses DualCtaStrip */}
      {page === 'job-seekers' && (
      <section id="job-seekers" className="job-seekers-section">
        <div className="container">
          <div className="section-header">
            {page === 'job-seekers' ? <h1 className="section-title">Submit Your CV - Get Matched</h1> : <h2 className="section-title">Submit Your CV - Get Matched</h2>}
            <p className="section-subtitle">Start Your International Career Journey</p>
          </div>
          <div className="cv-form-mount"><CVSubmissionForm /></div>
          <div className="cv-email-cta-container">
            <div className="cv-email-cta">
              <p className="cv-email-intro">
                Please send your CV and supporting documents—passport, education certificate, and recommendation letter—in PDF or DOC format. Include your full name, phone number, and the role or profession you are interested in.
              </p>
              <a href="mailto:info@adelgroups.com?subject=CV%20Submission" className="btn-email-cv">
                <Mail size={20} />
                Send your CV by email to info@adelgroups.com
              </a>
            </div>

            {/* What Happens Next + Quick Facts below the CTA */}
            <div className="cv-follow-up">
              <div className="info-card-compact">
                <h4 className="info-card-title">What Happens Next?</h4>
                <ol className="process-steps-list">
                  <li>
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <strong>We Review Your CV</strong>
                      <p>Our team reviews your application within 24-48 hours</p>
                    </div>
                  </li>
                  <li>
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <strong>Matching with Opportunities</strong>
                      <p>We match your profile with active job openings</p>
                    </div>
                  </li>
                  <li>
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <strong>We Contact You</strong>
                      <p>If matched, we'll call or email you to discuss next steps</p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="info-card-compact">
                <h4 className="info-card-title">Quick Facts</h4>
                <ul className="quick-facts-list">
                  <li><CheckCircle2 size={18} /> <span>Verified international employers</span></li>
                  <li><CheckCircle2 size={18} /> <span>Complete visa & travel support</span></li>
                  <li><CheckCircle2 size={18} /> <span>Ethical & transparent recruitment</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Contact Section — only on /contact; home uses DualCtaStrip */}
      {page === 'contact' && (
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            {page === 'contact' ? <h1 className="section-title">Get in Touch</h1> : <h2 className="section-title">Get in Touch</h2>}
            <p className="section-subtitle">Let's Discuss Your Manpower Needs</p>
          </div>

          {/* Contact Info Bar */}
          <div className="contact-info-bar">
            <a href="tel:+254117222777" className="contact-info-item">
              <Phone size={16} />
              <span>+254 117 222 777</span>
            </a>
            <a href="mailto:info@adelgroups.com" className="contact-info-item">
              <Mail size={16} />
              <span>info@adelgroups.com</span>
            </a>
            <a
              href={VIEW_PARK_TOWERS_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-info-item"
              aria-label="Open View Park Towers, Utaali Lane, Nairobi in Google Maps"
            >
              <MapPin size={16} />
              <span>{page === 'contact' ? 'Room 12, 6th floor, View Park Towers, Utaali Lane, Nairobi' : 'Nairobi, Kenya'}</span>
            </a>
          </div>

          {/* Contact page: map and full address */}
          {page === 'contact' && (
            <div className="contact-map-block">
              <p className="contact-address-text">
                We are located at <strong>Room 12, 6th floor, View Park Towers</strong>, Utaali Lane, Nairobi. Click the map to open in Google Maps.
              </p>
              <a
                href={VIEW_PARK_TOWERS_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-map-link"
                aria-label="Open View Park Towers, Utaali Lane, Nairobi in Google Maps"
              >
                <iframe
                  title="View Park Towers, Utaali Lane, Nairobi"
                  src="https://maps.google.com/maps?q=View+Park+Towers,Utaali+Lane,Nairobi&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  className="contact-map-iframe"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </a>
            </div>
          )}

          <div className="contact-content">
            <div className="contact-email-cta">
              <a href="mailto:info@adelgroups.com" className="btn-email-contact">
                <Mail size={20} />
                Email us at info@adelgroups.com
              </a>
            </div>
          </div>
        </div>
      </section>
      )}

      <SiteFooter />

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/254117222777" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default LandingPage;
